import React from 'react';
import { Document, Page, StyleSheet, Text, View, Image } from '@react-pdf/renderer';
import { format } from 'date-fns';
import * as locales from 'date-fns/locale';
import { InventoryOrderItem } from '@api/InventoryOrders/InventoryOrder';
import { DistributionCenter } from '@api/DistributionCenters/DistributionCenter';
import logoCircle from '@assets/img/logoCircle.png';

const styles = StyleSheet.create({
    body: {
        padding: 40,
        fontFamily: 'Helvetica'
    },
    header: {
        marginBottom: 5
    },
    logo: {
        width: 150,
        marginBottom: 20,
        alignSelf: 'center'
    },
    title: {
        fontSize: 24,
        fontWeight: 'bold',
        color: '#2c3e50',
        textAlign: 'center',
        marginBottom: 10
    },
    subtitle: {
        fontSize: 12,
        color: '#7f8c8d',
        textAlign: 'center',
        marginBottom: 20
    },
    infoContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 30,
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
        paddingBottom: 20
    },
    infoBoxLeft: {
        width: '60%'
    },
    infoBoxRight: {
        width: '40%',
        alignItems: 'flex-end' // Alinea el contenido a la derecha
    },
    infoLabel: {
        fontSize: 10,
        color: '#95a5a6',
        marginBottom: 5
    },
    infoText: {
        fontSize: 12,
        color: '#34495e'
    },
    tableHeader: {
        flexDirection: 'row',
        paddingVertical: 8,
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
        marginBottom: 5
    },
    tableRow: {
        flexDirection: 'row',
        paddingVertical: 8,
        borderBottomWidth: 1,
        borderBottomColor: '#f5f5f5'
    },
    col1: {
        width: '70%',
        fontSize: 12,
        color: '#555555' // Color gris más oscuro
    },
    col2: {
        width: '30%',
        fontSize: 12,
        textAlign: 'right',
        color: '#555555'
    },
    headerText: {
        fontSize: 13, // Un poco más grande que el texto normal
        color: '#666666', // Gris medio
        fontWeight: 'semibold'
    },
    footer: {
        position: 'absolute',
        bottom: 30,
        left: 0,
        right: 0,
        textAlign: 'center',
        fontSize: 10,
        color: '#95a5a6'
    },
    summary: {
        marginBottom: 15,
        paddingBottom: 10
    }
});

interface ShoppingListPDFProps {
    items: InventoryOrderItem[];
    distributionCenter?: DistributionCenter;
}

const ShoppingListPDF: React.FC<ShoppingListPDFProps> = ({ items, distributionCenter }) => {
    const currentDate = new Date();

    const formatDate = () => {
        return format(currentDate, "dd 'de' MMMM, yyyy", {
            locale: locales.es
        });
    };

    const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

    return (
        <Document>
            <Page style={styles.body}>
                <View style={styles.header}>
                    <Image style={styles.logo} src={logoCircle} />
                    <Text style={styles.title}>LISTA DE COMPRAS</Text>
                    <Text style={styles.subtitle}>Resumen consolidado de insumos solicitados</Text>

                    <View style={styles.infoContainer}>
                        <View style={styles.infoBoxLeft}>
                            <Text style={styles.infoLabel}>CENTRO DE DISTRIBUCIÓN</Text>
                            <Text style={styles.infoText}>{distributionCenter?.name || 'No especificado'}</Text>
                        </View>
                        <View style={styles.infoBoxRight}>
                            <Text style={styles.infoLabel}>FECHA DE GENERACIÓN</Text>
                            <Text style={styles.infoText}>{formatDate()}</Text>
                        </View>
                    </View>
                </View>
                <View style={{ marginBottom: 15 }}>
                    <Text style={styles.infoLabel}>TOTAL DE INSUMOS SOLICITADOS: {items.length}</Text>
                    <Text style={styles.infoLabel}>UNIDADES TOTALES: {totalItems}</Text>
                </View>

                <View style={styles.tableHeader}>
                    <Text style={[styles.headerText, styles.col1]}>INSUMO</Text>
                    <Text style={[styles.headerText, styles.col2]}>CANTIDAD</Text>
                </View>

                {items.map((item, index) => (
                    <View key={index} style={styles.tableRow}>
                        <Text style={styles.col1}>{item.input.name}</Text>
                        <Text style={styles.col2}>
                            {item.quantity} {item.input.presentation?.unit || 'un'}
                        </Text>
                    </View>
                ))}

                <Text style={styles.footer}>
                    Generado automáticamente el {format(currentDate, 'dd/MM/yyyy HH:mm')} | Sistema de Inventarios
                </Text>
            </Page>
        </Document>
    );
};

export default ShoppingListPDF;
